import { Accordion, Badge, Box, Button, Card, CloseButton, Flex, Group, SegmentedControl, Skeleton, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { IconDownload, IconExternalLink, IconFileCertificate, IconSearch } from '@tabler/icons-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  businessToday,
  daysBetween,
  formatBytes,
  type ClientFiledDossierDto,
  type ClientLibraryDocumentDto,
  type ClientLibraryDto,
  type ClientLibrarySectionDto,
  type RcValidityDto,
} from '@mexp/shared';
import { ErrorState } from '@/components/States';
import { openFile } from '@/features/files/files';
import { FileIcon } from '@/features/files/FileList';
import { notifyError } from '@/lib/notify';
import { useClientLibrary } from './api';
import { RcUploadModal } from './RcUploadModal';
import { fullDate } from './stage';

type Library = ReturnType<typeof useClientLibrary>;

const open = (fileId: string, disposition?: 'inline' | 'attachment') =>
  void openFile(fileId, disposition).catch((e) => notifyError(e, 'Could not open the file'));

function matches(d: ClientLibraryDocumentDto, q: string): boolean {
  const needle = q.trim().toLowerCase();
  return !needle || [d.name, d.product, d.country].some((v) => v?.toLowerCase().includes(needle));
}

/** Tab counts for the All dossiers page. */
export function libraryCounts(data: ClientLibraryDto | undefined): { dossiers?: number; certificates?: number; media?: number } {
  if (!data) return {};
  return {
    dossiers: data.dossiers.length,
    certificates: data.certificates.reduce((sum, s) => sum + s.items.length, 0),
    media: data.media.reduce((sum, s) => sum + s.items.length, 0),
  };
}

/** Open and Download for a document's file; a file still in Google Drive only opens there; a link opens. */
function FileActions({ doc }: { doc: ClientLibraryDocumentDto }) {
  if (doc.file) {
    if (doc.file.inDrive) {
      return (
        <Button size="xs" variant="default" rightSection={<IconExternalLink size={14} />} onClick={() => open(doc.file!.id)}>
          Open
        </Button>
      );
    }
    return (
      <>
        <Button size="xs" variant="default" onClick={() => open(doc.file!.id)}>
          Open
        </Button>
        <Button size="xs" color="teal" leftSection={<IconDownload size={14} />} onClick={() => open(doc.file!.id, 'attachment')}>
          Download
        </Button>
      </>
    );
  }
  if (doc.linkUrl) {
    return (
      <Button size="xs" variant="default" component="a" href={doc.linkUrl} target="_blank" rel="noopener noreferrer" rightSection={<IconExternalLink size={14} />}>
        Open link
      </Button>
    );
  }
  return null;
}

/**
 * One filed document with Open and Download. Files still in Google Drive open there. `children` render under the row,
 * inside its hairlines.
 */
function DocumentRow({
  doc,
  showCountry,
  requestId,
  children,
}: {
  doc: ClientLibraryDocumentDto;
  showCountry?: boolean;
  /** A dossier released through the portal: its files are on the request page. */
  requestId?: string;
  children?: ReactNode;
}) {
  const meta = [
    showCountry ? doc.country : null,
    doc.validity ? validityText(doc.validity) : null,
    doc.file && !doc.file.inDrive ? formatBytes(doc.file.sizeBytes) : null,
    `${requestId ? 'released' : 'added'} ${fullDate(doc.addedAt.slice(0, 10))}`,
    doc.file?.inDrive ? 'opens in Google Drive' : null,
  ].filter(Boolean);
  const row = (
    <Flex direction={{ base: 'column', xs: 'row' }} justify="space-between" align={{ base: 'stretch', xs: 'center' }} gap="sm">
      <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
        <FileIcon name={doc.name} />
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
            <Text fz="sm" fw={600} truncate="end">
              {doc.name}
            </Text>
            {doc.validity && <RcBadge validity={doc.validity} />}
          </Group>
          <Text fz="xs" c="dimmed" className="tabular">
            {meta.join(' · ')}
          </Text>
        </Stack>
      </Group>
      <Group gap={6} wrap="nowrap" style={{ flex: 'none' }} className="portal-file-actions">
        {!doc.file && requestId ? (
          <Button size="xs" variant="default" component={Link} to={`/requests/${requestId}`}>
            Open dossier
          </Button>
        ) : (
          <FileActions doc={doc} />
        )}
      </Group>
    </Flex>
  );
  return (
    <Box className="portal-delivery-file" py="sm">
      {row}
      {children}
    </Box>
  );
}

function validityText(v: RcValidityDto): string {
  return v.issueDate && v.expiryDate ? `issued ${fullDate(v.issueDate)} · valid until ${fullDate(v.expiryDate)}` : 'dates not recorded';
}

/** Expiring (due soon, yellow) and expired (overdue, orange); nothing for a valid or undated certificate. */
function RcBadge({ validity }: { validity: RcValidityDto }) {
  if (validity.status === 'expired') {
    return (
      <Badge color="orange" variant="light" size="sm" style={{ flex: 'none' }}>
        Expired
      </Badge>
    );
  }
  if (validity.status !== 'expiring' || !validity.expiryDate) return null;
  const days = daysBetween(businessToday(), validity.expiryDate);
  return (
    <Badge color="yellow" variant="light" size="sm" style={{ flex: 'none' }}>
      {days === 0 ? 'Expires today' : `Expires in ${days} day${days === 1 ? '' : 's'}`}
    </Badge>
  );
}

/** Under each filed dossier: its registration certificates, or a note that none is on file; and the upload button. */
/**
 * Under a filed dossier, only what needs doing: upload its registration certificate when none is on file, or the
 * renewed one when it expires within 90 days or has expired. Certificates on file are under Certificates.
 */
function RegistrationCertificateUpload({ dossier, onUpload }: { dossier: ClientFiledDossierDto; onUpload: (d: ClientFiledDossierDto) => void }) {
  const renewal = dossier.rcStatus === 'expiring' || dossier.rcStatus === 'expired';
  if (dossier.rcStatus !== 'missing' && !renewal) return null;
  const expiry = dossier.registrationCertificates[0]?.validity?.expiryDate ?? null;
  const days = expiry ? daysBetween(businessToday(), expiry) : null;
  return (
    <Group mt={6} ml={{ base: 0, xs: 42 }} gap="sm" justify="space-between" wrap="wrap">
      <Text fz="xs" c={renewal ? 'var(--mantine-color-yellow-light-color)' : 'dimmed'} fw={renewal ? 600 : 400}>
        {!renewal
          ? 'Registration certificate: not on file yet'
          : days !== null && days < 0
            ? `Registration certificate expired on ${fullDate(expiry!)}`
            : days === 0
              ? 'Registration certificate expires today'
              : `Registration certificate expires in ${days} day${days === 1 ? '' : 's'}`}
      </Text>
      <Button size="compact-xs" variant="light" leftSection={<IconFileCertificate size={14} />} onClick={() => onUpload(dossier)}>
        {renewal ? 'Upload renewed RC' : 'Upload RC'}
      </Button>
    </Group>
  );
}

/** A segmented control in a row from 36em and stacked full width on phones (CSS only: no first-paint jump). */
function Segments({ value, onChange, data }: { value: string; onChange: (v: string) => void; data: { value: string; label: string }[] }) {
  return (
    <>
      <SegmentedControl value={value} onChange={onChange} data={data} orientation="vertical" fullWidth hiddenFrom="xs" w="100%" />
      <SegmentedControl value={value} onChange={onChange} data={data} visibleFrom="xs" />
    </>
  );
}

/** Filed dossiers views: all, the ones without a certificate, and the ones whose certificate expires soon or expired. */
const RC_FILTERS = ['all', 'missing', 'renewal'] as const;
type RcFilter = (typeof RC_FILTERS)[number];
const needs = (d: ClientFiledDossierDto, f: RcFilter) =>
  f === 'all' || (f === 'missing' ? d.rcStatus === 'missing' : d.rcStatus === 'expiring' || d.rcStatus === 'expired');

function SearchBox({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  return (
    <TextInput
      aria-label={label}
      placeholder="Search file, product or country"
      leftSection={<IconSearch size={16} />}
      rightSection={value ? <CloseButton size="sm" aria-label="Clear search" onClick={() => onChange('')} /> : null}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      w={{ base: '100%', sm: 300 }}
    />
  );
}

function Loading() {
  return (
    <Card p={{ base: 'md', sm: 'xl' }}>
      <Stack gap="lg">
        <Skeleton h={72} />
        <Skeleton h={72} />
      </Stack>
    </Card>
  );
}

function NoMatch({ q, onClear }: { q: string; onClear: () => void }) {
  return (
    <Stack gap="xs" align="flex-start">
      <Text fz="sm">Nothing matches “{q.trim()}”.</Text>
      <Button size="compact-sm" variant="subtle" onClick={onClear}>
        Clear the search
      </Button>
    </Stack>
  );
}

/** One country's filed dossiers, shelved by product. */
interface CountryGroup {
  key: string;
  country: string | null;
  products: { product: string; docs: ClientFiledDossierDto[] }[];
  total: number;
  /** Dossiers with no registration certificate on file. */
  missing: number;
}

/** Countries in alphabetical order (dossiers with no country last), each with its products in alphabetical order. */
function byCountry(dossiers: ClientFiledDossierDto[]): CountryGroup[] {
  const groups = new Map<string, CountryGroup>();
  for (const d of dossiers) {
    const country = d.country?.trim() || null;
    const key = country?.toLowerCase() ?? '(none)';
    const group = groups.get(key) ?? { key, country, products: [], total: 0, missing: 0 };
    const product = d.product?.trim() || 'Other documents';
    let shelf = group.products.find((p) => p.product.toLowerCase() === product.toLowerCase());
    if (!shelf) group.products.push((shelf = { product, docs: [] }));
    shelf.docs.push(d);
    group.total += 1;
    if (d.rcStatus === 'missing') group.missing += 1;
    groups.set(key, group);
  }
  const byName = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });
  const sorted = [...groups.values()].sort((a, b) => (a.country === null ? 1 : b.country === null ? -1 : byName(a.country, b.country)));
  for (const g of sorted) g.products.sort((a, b) => byName(a.product, b.product));
  return sorted;
}

/** Dossiers as filed with the authority for the company: one section per country, which opens and closes. */
export function FiledDossiers({ library }: { library: Library }) {
  const [q, setQ] = useState('');
  const [params, setParams] = useSearchParams();
  const filter: RcFilter = (RC_FILTERS as readonly string[]).includes(params.get('rc') ?? '') ? (params.get('rc') as RcFilter) : 'all';
  const [uploadFor, setUploadFor] = useState<ClientFiledDossierDto | null>(null);
  const setFilter = (next: string) => {
    const out = new URLSearchParams(params);
    if (next === 'all') out.delete('rc');
    else out.set('rc', next);
    setParams(out, { replace: true });
  };
  const groups = useMemo(() => byCountry((library.data?.dossiers ?? []).filter((d) => needs(d, filter) && matches(d, q))), [library.data, q, filter]);
  const [open, setOpen] = useState<string[]>([]);
  // A search opens every country it finds something in; otherwise each country opens and closes on its own.
  const searching = q.trim() !== '';
  useEffect(() => {
    if (searching) setOpen(groups.map((g) => g.key));
  }, [searching, groups]);
  // A single country has nothing to fold away.
  const expanded = groups.length === 1 ? [groups[0]!.key] : open;
  const allOpen = groups.length > 0 && groups.every((g) => expanded.includes(g.key));

  if (library.isError) return <ErrorState error={library.error} onRetry={() => void library.refetch()} />;
  if (library.isLoading) return <Loading />;
  const all = library.data?.dossiers ?? [];
  const total = all.length;
  const count = (f: RcFilter) => all.filter((d) => needs(d, f)).length;

  return (
    <Card p={{ base: 'md', sm: 'xl' }}>
      {total === 0 ? (
        <Stack gap={6} align="flex-start">
          <Text fw={600}>No filed dossiers yet</Text>
          <Text fz="sm" c="dimmed" maw={560}>
            Dossiers Makin has filed with an authority for your company appear here.
          </Text>
        </Stack>
      ) : (
        <Stack gap="lg">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Segments
              value={filter}
              onChange={setFilter}
              data={[
                { value: 'all', label: `All · ${total}` },
                { value: 'missing', label: `RC missing · ${count('missing')}` },
                { value: 'renewal', label: `Renewal due · ${count('renewal')}` },
              ]}
            />
            <SearchBox value={q} onChange={setQ} label="Search filed dossiers" />
          </Group>
          <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
            <Text fz="sm" c="dimmed">
              {filter === 'missing'
                ? 'Dossiers with no registration certificate on file. Upload each one with its issue and expiry dates.'
                : filter === 'renewal'
                  ? 'Registration certificates that expire within 90 days or have expired. Upload the renewed certificate when you have it.'
                  : 'Dossiers filed with the authority or released by Makin, by country. Open a country to see its dossiers.'}
            </Text>
            {groups.length > 1 && (
              <Button size="compact-sm" variant="subtle" style={{ flex: 'none' }} onClick={() => setOpen(allOpen ? [] : groups.map((g) => g.key))}>
                {allOpen ? 'Collapse all' : 'Expand all'}
              </Button>
            )}
          </Group>
          {groups.length === 0 ? (
            q.trim() ? (
              <NoMatch q={q} onClear={() => setQ('')} />
            ) : (
              <Text fz="sm" c="dimmed">
                {filter === 'missing' ? 'Every dossier has a registration certificate on file.' : 'No registration certificate is due for renewal.'}
              </Text>
            )
          ) : (
            <Accordion multiple value={expanded} onChange={setOpen} chevronPosition="left" className="portal-countries">
              {groups.map((g) => (
                <Accordion.Item key={g.key} value={g.key}>
                  <Accordion.Control>
                    <Group justify="space-between" wrap="nowrap" gap="sm">
                      <Text fw={600} fz="md" lh={1.3} truncate="end">
                        {g.country ?? 'No country'}
                      </Text>
                      <Text fz="xs" c="dimmed" className="tabular" style={{ flex: 'none' }}>
                        {g.total} dossier{g.total === 1 ? '' : 's'}
                        {g.missing > 0 && filter !== 'missing' ? ` · ${g.missing} without RC` : ''}
                      </Text>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      {g.products.map((p) => (
                        <Stack key={p.product} gap={0}>
                          <Text fw={600} fz="sm" lh={1.3}>
                            {p.product}
                          </Text>
                          <Box>
                            {p.docs.map((d) => (
                              <DocumentRow key={d.id} doc={d} requestId={d.request?.id}>
                                <RegistrationCertificateUpload dossier={d} onUpload={setUploadFor} />
                              </DocumentRow>
                            ))}
                          </Box>
                        </Stack>
                      ))}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
        </Stack>
      )}
      <RcUploadModal dossier={uploadFor} onClose={() => setUploadFor(null)} />
    </Card>
  );
}

/** The product and country under a certificate's name, when the Data Bank has them. */
const certificateFor = (d: ClientLibraryDocumentDto) => [d.product, d.country].filter(Boolean).join(' · ');

/** A registration certificate's product, with its country under it. */
function ProductCell({ doc }: { doc: ClientLibraryDocumentDto }) {
  if (!doc.product && !doc.country) {
    return (
      <Text span fz="sm" c="dimmed">
        —
      </Text>
    );
  }
  return (
    <Stack gap={0}>
      <Text fz="sm" fw={500} lineClamp={2}>
        {doc.product ?? <Text span c="dimmed" inherit>Product not recorded</Text>}
      </Text>
      {doc.country && (
        <Text fz="xs" c="dimmed">
          {doc.country}
        </Text>
      )}
    </Stack>
  );
}

/** Issued and Valid till cells: the dates, with Expired or Expires in N days; dashes when none is recorded. */
function IssuedDate({ doc }: { doc: ClientLibraryDocumentDto }) {
  return doc.validity?.issueDate ? <>{fullDate(doc.validity.issueDate)}</> : <Text span c="dimmed" inherit>—</Text>;
}

function ValidTill({ doc }: { doc: ClientLibraryDocumentDto }) {
  if (!doc.validity?.expiryDate) {
    return (
      <Text span c="dimmed" inherit>
        {doc.validity ? 'Not recorded' : '—'}
      </Text>
    );
  }
  return (
    <Stack gap={4} align="flex-start">
      <span>{fullDate(doc.validity.expiryDate)}</span>
      <RcBadge validity={doc.validity} />
    </Stack>
  );
}

/**
 * From 48em, a table: document, issued, valid till and its file, and for registration certificates the product (and
 * country) in a column of its own. On phones, the same as stacked rows.
 */
function CertificateTable({ items, productColumn = false }: { items: ClientLibraryDocumentDto[]; productColumn?: boolean }) {
  // With a Product column the document cell is just the file; otherwise product and country sit under its name.
  const under = (d: ClientLibraryDocumentDto) => (productColumn ? '' : certificateFor(d));
  return (
    <>
      <Table visibleFrom="sm" verticalSpacing="sm" horizontalSpacing="sm" highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th pl={0} fz="xs" c="dimmed" fw={600}>
              Document
            </Table.Th>
            {productColumn && (
              <Table.Th fz="xs" c="dimmed" fw={600} w="24%">
                Product
              </Table.Th>
            )}
            <Table.Th fz="xs" c="dimmed" fw={600} w={132}>
              Issued
            </Table.Th>
            <Table.Th fz="xs" c="dimmed" fw={600} w={160}>
              Valid till
            </Table.Th>
            <Table.Th pr={0} fz="xs" c="dimmed" fw={600} w={196} ta="right">
              File
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((d) => (
            <Table.Tr key={d.id}>
              <Table.Td pl={0}>
                <Group gap="sm" wrap="nowrap">
                  <FileIcon name={d.name} />
                  <Stack gap={0} style={{ minWidth: 0 }}>
                    <Text fz="sm" fw={600} lineClamp={2} style={{ overflowWrap: 'anywhere' }}>
                      {d.name}
                    </Text>
                    {under(d) && (
                      <Text fz="xs" c="dimmed">
                        {under(d)}
                      </Text>
                    )}
                  </Stack>
                </Group>
              </Table.Td>
              {productColumn && (
                <Table.Td>
                  <ProductCell doc={d} />
                </Table.Td>
              )}
              <Table.Td fz="sm" className="tabular" style={{ whiteSpace: 'nowrap' }}>
                <IssuedDate doc={d} />
              </Table.Td>
              <Table.Td fz="sm" className="tabular" style={{ whiteSpace: 'nowrap' }}>
                <ValidTill doc={d} />
              </Table.Td>
              <Table.Td pr={0}>
                <Group gap={6} wrap="nowrap" justify="flex-end">
                  <FileActions doc={d} />
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Box hiddenFrom="sm">
        {items.map((d) => (
          <Box key={d.id} className="portal-delivery-file" py="sm">
            <Group gap="sm" wrap="nowrap" align="flex-start">
              <FileIcon name={d.name} />
              <Stack gap={6} style={{ minWidth: 0, flex: 1 }}>
                <Stack gap={0}>
                  <Text fz="sm" fw={600} style={{ overflowWrap: 'anywhere' }}>
                    {d.name}
                  </Text>
                  {certificateFor(d) && (
                    <Text fz="xs" c={productColumn ? undefined : 'dimmed'} fw={productColumn ? 500 : 400}>
                      {certificateFor(d)}
                    </Text>
                  )}
                </Stack>
                <Group gap="lg" wrap="wrap" align="flex-start" className="tabular">
                  <Stack gap={0}>
                    <Text fz="xs" c="dimmed">
                      Issued
                    </Text>
                    <Text fz="sm">
                      <IssuedDate doc={d} />
                    </Text>
                  </Stack>
                  <Stack gap={0}>
                    <Text fz="xs" c="dimmed">
                      Valid till
                    </Text>
                    <Text fz="sm" component="div">
                      <ValidTill doc={d} />
                    </Text>
                  </Stack>
                </Group>
                <Group gap={6} wrap="nowrap" className="portal-file-actions">
                  <FileActions doc={d} />
                </Group>
              </Stack>
            </Group>
          </Box>
        ))}
      </Box>
    </>
  );
}

function CertificateSection({
  section,
  company,
  caption,
  plain,
}: {
  section: ClientLibrarySectionDto;
  company: string | undefined;
  /** Replaces the line under the title (who the files are for). */
  caption?: string;
  /** A plain list of files (artworks, images) instead of the certificate table with its dates. */
  plain?: boolean;
}) {
  const showCountry = section.items.some((d) => d.country);
  return (
    <Card p={{ base: 'md', sm: 'xl' }}>
      <Stack gap="xs">
        <Stack gap={2}>
          <Title order={2} fz="lg">
            {section.label}
          </Title>
          <Text fz="sm" c="dimmed">
            {caption ?? (section.companyWide ? "Makin Laboratories' company certificates" : `Issued for ${company ?? 'your company'}`)}
            {` · ${section.items.length} file${section.items.length === 1 ? '' : 's'}`}
          </Text>
        </Stack>
        {section.items.length ? (
          plain ? (
            <Box>
              {section.items.map((d) => (
                <DocumentRow key={d.id} doc={d} showCountry={showCountry} />
              ))}
            </Box>
          ) : (
            <CertificateTable items={section.items} productColumn={section.key === 'registration'} />
          )
        ) : (
          <Text fz="sm" c="dimmed">
            None yet.
          </Text>
        )}
      </Stack>
    </Card>
  );
}

/**
 * Certificates in sub-tabs: Makin's accreditation certificates first, then each kind of the company's own certificates
 * that has any. The chosen sub-tab lives in the URL (`certs`); the search looks within it.
 */
export function ClientCertificates({ library, company }: { library: Library; company: string | undefined }) {
  const [q, setQ] = useState('');
  const [params, setParams] = useSearchParams();
  if (library.isError) return <ErrorState error={library.error} onRetry={() => void library.refetch()} />;
  if (library.isLoading) return <Loading />;

  const all = library.data?.certificates ?? [];
  const tabs = [...all.filter((s) => s.companyWide), ...all.filter((s) => !s.companyWide && s.items.length > 0)];
  const active = tabs.find((s) => s.key === params.get('certs')) ?? tabs[0];
  const select = (key: string) => {
    const next = new URLSearchParams(params);
    if (key === tabs[0]?.key) next.delete('certs');
    else next.set('certs', key);
    setParams(next, { replace: true });
    setQ('');
  };
  if (!active) return null;
  const shown = { ...active, items: active.items.filter((d) => matches(d, q)) };
  const options = tabs.map((s) => ({ value: s.key, label: `${s.label} · ${s.items.length}` }));

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
        <Segments value={active.key} onChange={select} data={options} />
        <SearchBox value={q} onChange={setQ} label={`Search ${active.label}`} />
      </Group>
      {q.trim() && !shown.items.length ? (
        <Card p={{ base: 'md', sm: 'xl' }}>
          <NoMatch q={q} onClear={() => setQ('')} />
        </Card>
      ) : (
        <CertificateSection section={shown} company={company} />
      )}
    </Stack>
  );
}

/** Approved artworks (PDF) and 3D images of commercial products Makin keeps for the company, each section in turn. */
export function ClientMedia({ library, company }: { library: Library; company: string | undefined }) {
  const [q, setQ] = useState('');
  if (library.isError) return <ErrorState error={library.error} onRetry={() => void library.refetch()} />;
  if (library.isLoading) return <Loading />;
  const sections = (library.data?.media ?? []).map((s) => ({ ...s, items: s.items.filter((d) => matches(d, q)) }));
  return (
    <Stack gap="lg">
      <Group justify="flex-end">
        <SearchBox value={q} onChange={setQ} label="Search artworks and 3D images" />
      </Group>
      {sections.map((s) => (
        <CertificateSection key={s.key} section={s} company={company} caption={`For ${company ?? 'your company'}`} plain />
      ))}
    </Stack>
  );
}
